// Capa de acceso a Supabase Storage — bucket "medios" (ver
// supabase/migrations/20260101000011_storage_medios.sql y 6.3 del doc de
// BD). Un único módulo para las dos carpetas de primer nivel que existen
// hoy (productos/, empresa/) en vez de uno por carpeta: la lógica de subida
// es idéntica, solo cambia la ruta.

import { supabaseClient } from '@plataforma/supabase-client';

import {
  PERFIL_BANNER,
  PERFIL_CATEGORIA,
  PERFIL_FAVICON,
  PERFIL_LOGO,
  PERFIL_PRODUCTO,
  comprimirImagen,
} from './imagen-optimizacion.js';

const BUCKET = 'medios';
const EXTENSIONES_PERMITIDAS = new Set(['jpg', 'jpeg', 'png', 'webp']);

/**
 * @param {File} archivo
 * @returns {string | null} null si la extensión no está permitida.
 */
function extensionValida(archivo) {
  const ext = archivo.name.split('.').pop()?.toLowerCase();
  return ext && EXTENSIONES_PERMITIDAS.has(ext) ? ext : null;
}

/**
 * Sube una imagen a una ruta *fija* (una sola por empresa, ej. logo,
 * favicon, logo alternativo) comprimiéndola primero según `perfil`.
 * Compartido por `subirLogoEmpresa`/`subirFaviconEmpresa`/
 * `subirLogoBlancoEmpresa`, que solo difieren en el nombre de archivo y el
 * perfil de compresión.
 *
 * @param {string} rutaFija - ruta completa dentro del bucket, sin extensión final resuelta (se agrega según el archivo comprimido).
 * @param {File} archivoOriginal
 * @param {import('./imagen-optimizacion.js').OpcionesCompresion} perfil
 * @returns {Promise<{ url: string | null, error: Error | null }>}
 */
async function subirImagenFija(rutaFija, archivoOriginal, perfil) {
  if (!extensionValida(archivoOriginal)) {
    return { url: null, error: new Error('Formato de imagen no permitido. Usa JPG, PNG o WEBP.') };
  }

  const archivo = await comprimirImagen(archivoOriginal, perfil);
  const ext = extensionValida(archivo) ?? extensionValida(archivoOriginal);

  const ruta = `${rutaFija}.${ext}`;
  const cliente = supabaseClient();

  // upsert: true — ruta fija (una sola versión por empresa), a diferencia
  // de las imágenes de producto/banner que son una colección abierta.
  const { error: errorSubida } = await cliente.storage.from(BUCKET).upload(ruta, archivo, {
    cacheControl: '3600',
    upsert: true,
  });

  if (errorSubida) return { url: null, error: errorSubida };

  const {
    data: { publicUrl },
  } = cliente.storage.from(BUCKET).getPublicUrl(ruta);

  // Cache-busting: como la ruta es fija, un archivo nuevo con el mismo
  // nombre serviría la versión vieja desde caché de navegador/CDN si no se
  // distingue la URL. Se agrega un parámetro de consulta con la marca de
  // tiempo — no afecta la ruta real del objeto en Storage.
  const url = `${publicUrl}?v=${Date.now()}`;
  return { url, error: null };
}

/**
 * Sube una imagen de producto a `productos/{empresaId}/{productoId}/` con
 * nombre aleatorio (evita colisiones y no depende del nombre original, que
 * puede traer espacios/tildes problemáticos en una URL).
 *
 * @param {string} empresaId
 * @param {string} productoId
 * @param {File} archivo
 * @returns {Promise<{ url: string | null, error: Error | null }>}
 */
export async function subirImagenProducto(empresaId, productoId, archivoOriginal) {
  if (!extensionValida(archivoOriginal)) {
    return { url: null, error: new Error('Formato de imagen no permitido. Usa JPG, PNG o WEBP.') };
  }

  // Comprime/redimensiona en el navegador antes de subir — ver
  // imagen-optimizacion.js. 1600px es de sobra para la galería de producto
  // (sp-galeria__principal, aspect-ratio 1:1) incluso en pantallas 2x.
  const archivo = await comprimirImagen(archivoOriginal, PERFIL_PRODUCTO);
  const ext = extensionValida(archivo) ?? extensionValida(archivoOriginal);

  const ruta = `productos/${empresaId}/${productoId}/${crypto.randomUUID()}.${ext}`;
  const cliente = supabaseClient();

  const { error: errorSubida } = await cliente.storage.from(BUCKET).upload(ruta, archivo, {
    cacheControl: '31536000', // 1 año — la CDN/Cloudflare cachea agresivo, el nombre ya es único
    upsert: false,
  });

  if (errorSubida) return { url: null, error: errorSubida };

  const {
    data: { publicUrl },
  } = cliente.storage.from(BUCKET).getPublicUrl(ruta);

  return { url: publicUrl, error: null };
}

/**
 * @param {string} empresaId
 * @param {File} archivo
 * @returns {Promise<{ url: string | null, error: Error | null }>}
 */
export async function subirLogoEmpresa(empresaId, archivoOriginal) {
  // El logo se muestra chico (header, favicon-like usages) — 512px alcanza
  // incluso para pantallas 2x sobre el tamaño real que ocupa en el layout.
  return subirImagenFija(`empresa/${empresaId}/logo`, archivoOriginal, PERFIL_LOGO);
}

/**
 * Fase 10 (Tematización visual) — favicon por empresa
 * (`empresa_perfil.favicon_url`, columna provista desde la Fase 2 pero sin
 * consumidor hasta ahora). El storefront lo aplica dinámicamente al
 * `<link rel="icon">` — ver `apps/sitio-publico/src/js/tema.js`.
 *
 * @param {string} empresaId
 * @param {File} archivoOriginal
 * @returns {Promise<{ url: string | null, error: Error | null }>}
 */
export async function subirFaviconEmpresa(empresaId, archivoOriginal) {
  return subirImagenFija(`empresa/${empresaId}/favicon`, archivoOriginal, PERFIL_FAVICON);
}

/**
 * Fase 10 (Tematización visual) — variante del logo para fondos oscuros o
 * del color primario de la tienda (`empresa_perfil.logo_blanco_url`,
 * misma situación que el favicon: columna sin consumidor hasta ahora).
 * Opcional: si no se sube, el storefront usa el logo normal en todos los
 * fondos.
 *
 * @param {string} empresaId
 * @param {File} archivoOriginal
 * @returns {Promise<{ url: string | null, error: Error | null }>}
 */
export async function subirLogoBlancoEmpresa(empresaId, archivoOriginal) {
  return subirImagenFija(`empresa/${empresaId}/logo-blanco`, archivoOriginal, PERFIL_LOGO);
}

/**
 * Imagen de banner — vive bajo `empresa/{empresaId}/banners/` (dominio
 * "empresa" de la ruta, igual que el logo, porque la política de Storage
 * solo distingue el primer y segundo segmento de la ruta — ver
 * 20260101000011_storage_medios.sql — todo lo que va después es libre).
 *
 * @param {string} empresaId
 * @param {File} archivo
 * @returns {Promise<{ url: string | null, error: Error | null }>}
 */
export async function subirImagenBanner(empresaId, archivoOriginal) {
  if (!extensionValida(archivoOriginal)) {
    return { url: null, error: new Error('Formato de imagen no permitido. Usa JPG, PNG o WEBP.') };
  }

  // Los banners son el elemento más pesado del home (hero a todo ancho) —
  // 1920px cubre pantallas grandes sin cargar el original de la cámara.
  const archivo = await comprimirImagen(archivoOriginal, PERFIL_BANNER);
  const ext = extensionValida(archivo) ?? extensionValida(archivoOriginal);

  const ruta = `empresa/${empresaId}/banners/${crypto.randomUUID()}.${ext}`;
  const cliente = supabaseClient();

  const { error: errorSubida } = await cliente.storage.from(BUCKET).upload(ruta, archivo, {
    cacheControl: '31536000',
    upsert: false,
  });

  if (errorSubida) return { url: null, error: errorSubida };

  const {
    data: { publicUrl },
  } = cliente.storage.from(BUCKET).getPublicUrl(ruta);

  return { url: publicUrl, error: null };
}

/**
 * Imagen de categoría destacada — vive bajo `empresa/{empresaId}/categorias/`
 * (mismo patrón y misma política de Storage que los banners: la ruta
 * comienza por `empresa/{empresaId}`). Nombre aleatorio para no chocar con
 * el nombre de la categoría (que puede traer tildes/espacios) y omitir
 * cachés cuando se reemplaza la imagen.
 *
 * @param {string} empresaId
 * @param {File} archivoOriginal
 * @returns {Promise<{ url: string | null, error: Error | null }>}
 */
export async function subirImagenCategoria(empresaId, archivoOriginal) {
  if (!extensionValida(archivoOriginal)) {
    return { url: null, error: new Error('Formato de imagen no permitido. Usa JPG, PNG o WEBP.') };
  }

  // La tarjeta se muestra a 250–400px de ancho (object-fit cover) — 1200px
  // de máximo cubre pantallas 2x sin cargar la foto original de la cámara.
  const archivo = await comprimirImagen(archivoOriginal, PERFIL_CATEGORIA);
  const ext = extensionValida(archivo) ?? extensionValida(archivoOriginal);

  const ruta = `empresa/${empresaId}/categorias/${crypto.randomUUID()}.${ext}`;
  const cliente = supabaseClient();

  const { error: errorSubida } = await cliente.storage.from(BUCKET).upload(ruta, archivo, {
    cacheControl: '31536000',
    upsert: false,
  });

  if (errorSubida) return { url: null, error: errorSubida };

  const {
    data: { publicUrl },
  } = cliente.storage.from(BUCKET).getPublicUrl(ruta);

  return { url: publicUrl, error: null };
}

/**
 * @param {string} rutaStorage - ruta completa dentro del bucket, ej.
 *   `productos/{empresaId}/{productoId}/{archivo}`.
 */
export async function eliminarArchivo(rutaStorage) {
  return supabaseClient().storage.from(BUCKET).remove([rutaStorage]);
}

/**
 * Extrae la ruta dentro del bucket a partir de una URL pública de Storage,
 * para poder borrarla luego con `eliminarArchivo`.
 *
 * @param {string} urlPublica
 * @returns {string | null}
 */
export function rutaDesdeUrlPublica(urlPublica) {
  const marcador = `/object/public/${BUCKET}/`;
  const indice = urlPublica.indexOf(marcador);
  if (indice === -1) return null;
  return decodeURIComponent(urlPublica.slice(indice + marcador.length).split('?')[0]);
}
